import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface Facility {
  id: string;
  name: string;
  nameEn: string;
  icon: string;
}

interface FacilitiesContextType {
  facilities: Facility[];
  loading: boolean;
  refreshFacilities: () => Promise<void>;
  addFacility: (facility: Omit<Facility, 'id'>) => Promise<void>;
  updateFacility: (id: string, facility: Partial<Facility>) => Promise<void>;
  deleteFacility: (id: string) => Promise<void>;
}

const FacilitiesContext = createContext<FacilitiesContextType | undefined>(undefined);

export function FacilitiesProvider({ children }: { children: ReactNode }) {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('facilities')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const mappedFacilities = (data || []).map((f: any) => ({
        id: f.id,
        name: f.name,
        nameEn: f.name_en || '',
        icon: f.icon || 'Building2',
      }));
      
      setFacilities(mappedFacilities);
    } catch (err) {
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const addFacility = async (facility: Omit<Facility, 'id'>) => {
    const newFacilityData = {
      id: Date.now().toString(),
      name: facility.name,
      name_en: facility.nameEn,
      icon: facility.icon,
    };
    
    const { data, error } = await supabase
      .from('facilities')
      .insert(newFacilityData)
      .select()
      .single();
    
    if (error) throw error;
    
    setFacilities(prev => [{
      id: data.id,
      name: data.name,
      nameEn: data.name_en,
      icon: data.icon,
    }, ...prev]);
  };

  const updateFacility = async (id: string, facility: Partial<Facility>) => {
    const { error } = await supabase
      .from('facilities')
      .update({
        name: facility.name,
        name_en: facility.nameEn,
        icon: facility.icon,
      })
      .eq('id', id);
    
    if (error) throw error;
    
    setFacilities(prev => prev.map(f => 
      f.id === id ? { ...f, ...facility } : f
    ));
  };

  const deleteFacility = async (id: string) => {
    const { error } = await supabase.from('facilities').delete().eq('id', id);
    if (error) throw error;
    setFacilities(prev => prev.filter(f => f.id !== id));
  };

  useEffect(() => {
    fetchFacilities();
  }, []);

  return (
    <FacilitiesContext.Provider value={{
      facilities,
      loading,
      refreshFacilities: fetchFacilities,
      addFacility,
      updateFacility,
      deleteFacility
    }}>
      {children}
    </FacilitiesContext.Provider>
  );
}

export function useFacilities() {
  const context = useContext(FacilitiesContext);
  if (context === undefined) {
    throw new Error('useFacilities must be used within a FacilitiesProvider');
  }
  return context;
}
